import cx from 'classnames';
import styles from './LotteryGameRoomBodyWinners.module.scss';

import { FC, useContext, useEffect, useMemo } from 'react';

import { AppContext } from '@app/providers';
import { setGameRestarting, useAppDispatch, useAppSelector } from '@app/store';
import { CreateNewGameProps } from '@entities/game';
import { Button } from '@shared/Button';
import {
  lotteryAutoplayGameRemoveCurrent,
  setFirstShowWinners,
  setWinnersScreen,
} from '@features/LotteryGame/store';
import { GameRoom, LotteryFinishedState } from '@features/LotteryGame/types';
import { vibrateImpact } from '@utils/vibrate';
import { useAuthControllerWhoamiQuery, UserDto } from '@app/store/api/tunedRestApi';
import { setHideTooltip } from '@pages/JackpotPage/store/jackpotSlice';
import { LotteryGameRoomBodyTopWinner } from './components/LotteryGameRoomBodyWinner/LotteryGameRoomBodyTopWinner';
import { LotteryGameRoomBodyWinner } from './components/LotteryGameRoomBodyWinner/LotteryGameRoomBodyWinner';
import { getGroupedWinners } from './components/LotteryGameRoomBodyWinner/lib';
import { useTranslation } from 'react-i18next';

interface LotteryGameRoomBodyWinnersProps extends CreateNewGameProps {
  user?: UserDto;
  avatar?: string;
  gameRoom: GameRoom;
  firstShowWinners: boolean;
  finishState: LotteryFinishedState;
  luckyTicketNumber?: string;
  multiplier?: number;
  isAutoplay: boolean;
}

export const LotteryGameRoomBodyWinners: FC<LotteryGameRoomBodyWinnersProps> = ({
  user,
  avatar,
  gameRoom,
  turnType,
  lotteryApi,
  finishState,
  firstShowWinners,
  luckyTicketNumber,
  multiplier,
  isAutoplay,
}) => {
  const dispatch = useAppDispatch();
  const { isTelegramBot } = useContext(AppContext);
  const { refetch: refetchUser } = useAuthControllerWhoamiQuery();

  const { t } = useTranslation('mainPage');

  useEffect(() => {
    if (firstShowWinners) refetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameRoom.id]);

  useEffect(() => {
    dispatch(setWinnersScreen({ gameId: gameRoom.id, value: true }));

    return () => {
      dispatch(setWinnersScreen({ gameId: gameRoom.id, value: false }));
    };
  }, [gameRoom.id]);

  useEffect(() => {
    if (!isAutoplay) return;

    const timer = setTimeout(() => {
      dispatch(lotteryAutoplayGameRemoveCurrent());
    }, 2000);

    return () => {
      clearTimeout(timer);
    };
  }, [gameRoom.id, dispatch]);

  useEffect(() => {
    let vibrationTimer: NodeJS.Timeout;
    let replayTimer: NodeJS.Timeout;
    if (firstShowWinners) {
      const multipleVibrations = () => {
        let count = 0;
        const maxVibrations = 4;
        const interval = 330;

        const vibrateWithInterval = () => {
          if (count < maxVibrations) {
            vibrateImpact('soft');
            count++;
            vibrationTimer = setTimeout(vibrateWithInterval, interval);
          }
        };

        vibrateWithInterval();
      };
      multipleVibrations();
      replayTimer = setTimeout(multipleVibrations, 1820);
    }
    dispatch(setFirstShowWinners({ gameId: finishState.gameRoomId, firstShow: false }));

    return () => {
      if (vibrationTimer) clearTimeout(vibrationTimer);
      if (replayTimer) clearTimeout(replayTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const gameRestarting = useAppSelector((state) => state.states.gameRestarting);

  const placeArrays = gameRoom.state.superTicketState?.multiplyPlaces;

  const groupedWinners = useMemo(
    () =>
      finishState.winnerForMatches
        ? getGroupedWinners(finishState.winners)
        : {
            firstPlacedWinner: undefined,
            otherWinners: finishState.winners.map((winner, index) => {
              return {
                place: index + 1,
                winner,
              };
            }),
          },
    [finishState.winners, finishState.winnerForMatches],
  );

  return (
    <div
      className={cx(styles.lgrbwContainer, {
        [styles.lgrbwContainerSite]: !isTelegramBot,
      })}
    >
      <div
        onScroll={() => dispatch(setHideTooltip())}
        className={cx(styles.lgrbwList, { [styles.lgrbwListSite]: !isTelegramBot })}
      >
        {groupedWinners?.firstPlacedWinner && (
          <LotteryGameRoomBodyTopWinner
            user={user}
            avatar={avatar}
            userDisplayNumber={user?.displayNumber}
            placesWithWinNumbers={groupedWinners.firstPlacedWinner.placesWithWinNumbers}
            winnerPlayer={groupedWinners.firstPlacedWinner.winner.player}
            matchPrizeInfo={
              finishState.winnerForMatches
                ? {
                    prize: finishState.winnerForMatches.prize.totalForMatches || '0',
                    orderedMatches:
                      finishState.winnerForMatches.prize.orderedMatches || '0',
                    unorderedMatches:
                      finishState.winnerForMatches.prize.unorderedMatches || '0',
                  }
                : undefined
            }
            ticketWin={groupedWinners.firstPlacedWinner.totalFirstWinnerPrize}
            jackpotsArray={groupedWinners.firstPlacedWinner.jackpotsArray}
            luckyTicketNumber={luckyTicketNumber}
            multiplier={multiplier}
          />
        )}
        {groupedWinners?.otherWinners.map(({ winner, place }, i) => {
          return (
            <LotteryGameRoomBodyWinner
              user={user}
              key={i + 1}
              index={i}
              place={place}
              avatar={avatar}
              winNumber={winner.winNumber}
              winnerPlayer={winner.player}
              jackpots={winner.prize?.jackpots}
              prize={Number(winner.prize?.money)}
              userDisplayNumber={user?.displayNumber}
              luckyTicketNumber={luckyTicketNumber}
              multiplier={multiplier}
              placeArrays={placeArrays}
            />
          );
        })}
      </div>

      {!isAutoplay && (
        <Button
          title={t('again')}
          variant="custom"
          loading={gameRestarting}
          onClick={() => {
            dispatch(setGameRestarting(true));
            lotteryApi.findGame(gameRoom.bet, turnType, gameRoom.id);
            vibrateImpact('soft');
          }}
          className={styles.lgrbwAgainBtn}
        />
      )}
    </div>
  );
};
